import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ImagePlus,
  Info,
  Layers,
  Package,
  Plus,
  Settings,
  Tag,
  Trash2,
  Wallet,
} from "lucide-react";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import type { ProductVariationDto, ProductRemovableIngredientDto } from "@finora/types";
import { api } from "../../lib/api";
import { UnitCombobox } from "../../components/UnitCombobox";
import { ProductAttributesSection } from "./ProductAttributesSection";

type TabKey = "osnovno" | "cene" | "varijacije" | "zalihe" | "seo" | "dodatno";

const TABS: { key: TabKey; label: string; icon: typeof Package }[] = [
  { key: "osnovno", label: "Osnovni podaci", icon: Info },
  { key: "cene", label: "Cene i porezi", icon: Wallet },
  { key: "varijacije", label: "Varijacije i dodaci", icon: Layers },
  { key: "zalihe", label: "Zalihe", icon: Package },
  { key: "seo", label: "SEO / bar kod", icon: Tag },
  { key: "dodatno", label: "Dodatne opcije", icon: Settings },
];

const inputClass =
  "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const labelClass = "mb-1 block text-sm font-medium text-slate-700";
const requiredLabelClass = "mb-1 block text-sm font-medium text-red-600";

/**
 * Puna stranica za kreiranje/izmenu artikla (28.09.2026, modul Artikli -
 * varijacije i rok trajanja). Zamenjuje stari NewProductModal - taj fajl je
 * namerno ostavljen u repozitorijumu (neiskorišćen) jer ga zadatak nije
 * tražio da se briše. Layout: leva vertikalna navigacija kroz tabove, isti
 * kao mockup ("sekcije kao na levoj strani"), za razliku od horizontalnih
 * tabova na Podešavanjima (CompanySettingsPage) - ta stranica je uzeta kao
 * referenca za STIL (boje/dugmad), ne za raspored tabova.
 */
export function ProductEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<TabKey>("osnovno");

  const { data: product, isLoading } = useQuery({
    queryKey: ["products", id],
    queryFn: () => api.getProduct(id!),
    enabled: isEditMode,
  });

  const { data: taxRates } = useQuery({ queryKey: ["tax-rates"], queryFn: () => api.listTaxRates() });
  const { data: categories } = useQuery({
    queryKey: ["product-categories"],
    queryFn: () => api.listProductCategories(),
  });

  // --- Osnovni podaci ---
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isService, setIsService] = useState(false);
  const [unit, setUnit] = useState("kom");
  const [barcode, setBarcode] = useState("");
  const [sku, setSku] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // --- Cene i porezi ---
  const [unitPriceGross, setUnitPriceGross] = useState("");
  const [taxRateId, setTaxRateId] = useState("");
  const [description, setDescription] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [purchaseDiscountPercent, setPurchaseDiscountPercent] = useState("");

  // --- Zalihe ---
  const [minStockQuantity, setMinStockQuantity] = useState("");
  const [ignoreStockLimit, setIgnoreStockLimit] = useState(false);

  // --- SEO / bar kod ---
  const [metaTitle, setMetaTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [showImageOnReceipt, setShowImageOnReceipt] = useState(false);

  // --- Dodatne opcije ---
  const [hasWarranty, setHasWarranty] = useState(false);
  const [warrantyDuration, setWarrantyDuration] = useState("");
  const [warrantyUnit, setWarrantyUnit] = useState<"YEARS" | "MONTHS">("YEARS");
  const [isFeatured, setIsFeatured] = useState(false);
  const [allowDiscount, setAllowDiscount] = useState(true);
  const [internalNote, setInternalNote] = useState("");
  const [shelfLifeMode, setShelfLifeMode] = useState<"NONE" | "DATE" | "DAYS">("NONE");
  const [shelfLifeDate, setShelfLifeDate] = useState("");
  const [shelfLifeDays, setShelfLifeDays] = useState("");

  // Popuni formu kad se učitava artikal (izmena).
  useEffect(() => {
    if (!product) return;
    setName(product.name);
    setCategoryId(product.categoryId ?? "");
    setIsService(product.isService);
    setUnit(product.unit);
    setBarcode(product.barcode ?? "");
    setSku(product.sku);
    setImageUrl(product.imageUrl ?? null);
    setUnitPriceGross(product.unitPriceGross);
    setTaxRateId(product.taxRateId);
    setDescription(product.description ?? "");
    setPurchasePrice(product.purchasePrice ?? "");
    setPurchaseDiscountPercent(product.purchaseDiscountPercent ?? "");
    setMinStockQuantity(product.minStockQuantity ?? "");
    setIgnoreStockLimit(product.ignoreStockLimit);
    setMetaTitle(product.metaTitle ?? "");
    setShortDescription(product.shortDescription ?? "");
    setTagsInput((product.tags ?? []).join(", "));
    setSortOrder(String(product.sortOrder ?? 0));
    setShowImageOnReceipt(product.showImageOnReceipt);
    setHasWarranty(product.hasWarranty);
    setWarrantyDuration(product.warrantyDuration ? String(product.warrantyDuration) : "");
    setWarrantyUnit(product.warrantyUnit);
    setIsFeatured(product.isFeatured);
    setAllowDiscount(product.allowDiscount);
    setInternalNote(product.internalNote ?? "");
    setShelfLifeMode(product.shelfLifeMode);
    setShelfLifeDate(product.shelfLifeDate ?? "");
    setShelfLifeDays(product.shelfLifeDays ? String(product.shelfLifeDays) : "");
  }, [product]);

  const createCategory = useMutation({
    mutationFn: (name: string) => api.createProductCategory({ name }),
    onSuccess: (cat) => {
      queryClient.invalidateQueries({ queryKey: ["product-categories"] });
      setCategoryId(cat.id);
      setNewCategoryName("");
    },
  });

  function buildPayload() {
    return {
      name,
      categoryId: categoryId || null,
      isService,
      unit,
      barcode: barcode || null,
      sku: sku || undefined,
      unitPriceGross: unitPriceGross.replace(",", "."),
      taxRateId,
      description: description || null,
      purchasePrice: purchasePrice ? purchasePrice.replace(",", ".") : null,
      purchaseDiscountPercent: purchaseDiscountPercent ? purchaseDiscountPercent.replace(",", ".") : null,
      minStockQuantity: minStockQuantity ? minStockQuantity.replace(",", ".") : null,
      ignoreStockLimit,
      metaTitle: metaTitle || null,
      shortDescription: shortDescription || null,
      tags: tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      sortOrder: Number(sortOrder) || 0,
      showImageOnReceipt,
      hasWarranty,
      warrantyDuration: hasWarranty && warrantyDuration ? Number(warrantyDuration) : null,
      warrantyUnit,
      isFeatured,
      allowDiscount,
      internalNote: internalNote || null,
      shelfLifeMode,
      shelfLifeDate: shelfLifeMode === "DATE" ? shelfLifeDate || null : null,
      shelfLifeDays: shelfLifeMode === "DAYS" && shelfLifeDays ? Number(shelfLifeDays) : null,
    };
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      return isEditMode ? api.updateProduct(id!, payload) : api.createProduct(payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      if (!isEditMode) {
        // Nakon prvog čuvanja artikal dobija ID - prebaci na izmenu iste
        // stranice da se otključaju Varijacije/Dodaci/Sastojci tabovi.
        navigate(`/artikli/${saved.id}/izmeni`, { replace: true });
      }
    },
  });

  async function handleImageSelected(file: File) {
    if (!isEditMode) {
      window.alert("Prvo sačuvajte artikal, pa mu dodajte sliku kroz izmenu.");
      return;
    }
    setUploadingImage(true);
    try {
      const updated = await api.uploadProductImage(id!, file);
      setImageUrl(updated.imageUrl ?? null);
      queryClient.invalidateQueries({ queryKey: ["products"] });
    } finally {
      setUploadingImage(false);
    }
  }

  const canSubmit = name.trim().length > 0 && unitPriceGross.trim().length > 0 && !!taxRateId && !!categoryId;

  if (isEditMode && isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/artikli")}
          aria-label="Nazad na artikle"
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {isEditMode ? `Izmena artikla${product ? `: ${product.name}` : ""}` : "Novi artikal"}
          </h1>
          <p className="text-sm text-slate-500">Podaci se koriste na POS-u, ponudama i fakturama.</p>
        </div>
      </div>

      <div className="flex flex-col gap-4 md:flex-row">
        <nav className="flex shrink-0 flex-row gap-1 overflow-x-auto md:w-56 md:flex-col md:overflow-visible">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm font-medium ${
                tab === t.key ? "bg-brand-light text-brand" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <t.icon className="h-4 w-4" aria-hidden />
              {t.label}
            </button>
          ))}
        </nav>

        <div className="flex-1 rounded-xl border border-border bg-surface p-5 shadow-subtle">
          {tab === "osnovno" && (
            <div className="space-y-5">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageSelected(file);
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-dashed border-border bg-app-bg text-slate-400 hover:border-brand hover:text-brand"
                  >
                    {uploadingImage ? (
                      <span className="text-xs">...</span>
                    ) : imageUrl ? (
                      <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <ImagePlus className="h-6 w-6" aria-hidden />
                    )}
                  </button>
                </div>
                <div className="flex-1">
                  <label className={requiredLabelClass}>Naziv *</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={requiredLabelClass}>Kategorija *</label>
                <div className="flex gap-2">
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Izaberi kategoriju...</option>
                    {categories?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mt-2 flex gap-2">
                  <input
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Nova kategorija..."
                    className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => newCategoryName.trim() && createCategory.mutate(newCategoryName.trim())}
                    disabled={!newCategoryName.trim() || createCategory.isPending}
                    className="flex items-center gap-1 rounded-lg border border-border px-3 text-sm hover:bg-app-bg disabled:opacity-50"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden /> Dodaj
                  </button>
                </div>
                {!categoryId && (
                  <p className="mt-1 text-xs text-amber-600">
                    Kategorija je obavezna za nove artikle (ranije nije postojala na formi).
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-end gap-6">
                <div>
                  <label className={labelClass}>Tip</label>
                  <div className="flex h-10 items-center gap-4">
                    <label className="flex items-center gap-1.5 text-sm text-slate-700">
                      <input type="radio" checked={!isService} onChange={() => setIsService(false)} className="h-4 w-4 text-brand focus:ring-brand" />
                      Roba
                    </label>
                    <label className="flex items-center gap-1.5 text-sm text-slate-700">
                      <input type="radio" checked={isService} onChange={() => setIsService(true)} className="h-4 w-4 text-brand focus:ring-brand" />
                      Usluga
                    </label>
                  </div>
                </div>
                <div className="w-48">
                  <label className={labelClass}>Jedinica mere</label>
                  <UnitCombobox value={unit} onChange={setUnit} />
                </div>
                <div className="w-48">
                  <label className={labelClass}>Bar kod</label>
                  <input value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="npr. 8601234567890" className={inputClass} />
                </div>
                <div className="w-48">
                  <label className={labelClass}>Šifra (SKU)</label>
                  <input
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    placeholder={isEditMode ? undefined : "auto-generisano ako ostavite prazno"}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          )}

          {tab === "cene" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={requiredLabelClass}>Prodajna cena (sa PDV) *</label>
                  <div className="flex">
                    <span className="flex h-10 items-center rounded-l-lg border border-r-0 border-border bg-app-bg px-3 text-sm text-slate-500">RSD</span>
                    <input
                      value={unitPriceGross}
                      onChange={(e) => setUnitPriceGross(e.target.value)}
                      placeholder="0.00"
                      className="h-10 w-full rounded-r-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className={requiredLabelClass}>Poreska stopa *</label>
                  <select value={taxRateId} onChange={(e) => setTaxRateId(e.target.value)} className={inputClass}>
                    <option value="">Izaberi stopu...</option>
                    {taxRates?.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.isVatExempt ? t.name : `${t.ratePercent}%`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Opis</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h3 className="mb-3 text-sm font-semibold text-slate-800">Nabavna cena (samo za vas, kupac je ne vidi)</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>Nabavna cena</label>
                    <input value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} placeholder="0.00" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Rabat od dobavljača (%)</label>
                    <input value={purchaseDiscountPercent} onChange={(e) => setPurchaseDiscountPercent(e.target.value)} placeholder="0" className={inputClass} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "varijacije" && (
            <VarijacijeIDodaciTab productId={id} />
          )}

          {tab === "zalihe" && (
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Minimalna količina na zalihama</label>
                <input
                  value={minStockQuantity}
                  onChange={(e) => setMinStockQuantity(e.target.value)}
                  placeholder="npr. 5"
                  className="h-10 w-48 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Trenutno samo informativno polje (bez automatskog upozorenja) - upravljanje stvarnim stanjem zaliha ostaje kroz{" "}
                  <a href="/prijem-robe" className="text-brand hover:underline">Prijem robe</a>.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" checked={ignoreStockLimit} onChange={(e) => setIgnoreStockLimit(e.target.checked)} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" />
                Uvek dostupno (zanemari limit zaliha)
              </label>
            </div>
          )}

          {tab === "seo" && (
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Meta naslov</label>
                <input value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Kratak opis</label>
                <textarea value={shortDescription} onChange={(e) => setShortDescription(e.target.value)} rows={2} className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
              </div>
              <div>
                <label className={labelClass}>Oznake (tagovi, odvojene zarezom)</label>
                <input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="npr. akcija, novo, sezona" className={inputClass} />
              </div>
              <div className="flex flex-wrap items-end gap-6">
                <div className="w-40">
                  <label className={labelClass}>Redosled prikaza</label>
                  <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className={inputClass} />
                </div>
                <label className="flex h-10 items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={showImageOnReceipt} onChange={(e) => setShowImageOnReceipt(e.target.checked)} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" />
                  Prikaži sliku na računu
                </label>
              </div>
            </div>
          )}

          {tab === "dodatno" && (
            <div className="space-y-5">
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" />
                  Istaknut artikal
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={allowDiscount} onChange={(e) => setAllowDiscount(e.target.checked)} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" />
                  Dozvoli popust na artikal
                </label>
              </div>

              <div>
                <label className={labelClass}>Interna napomena (vidljiva samo vama)</label>
                <textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={3} className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
              </div>

              <div className="border-t border-border pt-4">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={hasWarranty} onChange={(e) => setHasWarranty(e.target.checked)} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" />
                  Garancija
                </label>
                {hasWarranty && (
                  <div className="mt-2 flex items-center gap-3">
                    <input value={warrantyDuration} onChange={(e) => setWarrantyDuration(e.target.value)} placeholder="npr. 2" className="h-10 w-24 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none" />
                    <select value={warrantyUnit} onChange={(e) => setWarrantyUnit(e.target.value as "YEARS" | "MONTHS")} className="h-10 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none">
                      <option value="YEARS">Godine</option>
                      <option value="MONTHS">Meseci</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-4">
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Rok trajanja</h3>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-1.5 text-sm text-slate-700">
                    <input type="radio" checked={shelfLifeMode === "NONE"} onChange={() => setShelfLifeMode("NONE")} className="h-4 w-4 text-brand focus:ring-brand" />
                    Nema roka
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-slate-700">
                    <input type="radio" checked={shelfLifeMode === "DATE"} onChange={() => setShelfLifeMode("DATE")} className="h-4 w-4 text-brand focus:ring-brand" />
                    Tačan datum
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-slate-700">
                    <input type="radio" checked={shelfLifeMode === "DAYS"} onChange={() => setShelfLifeMode("DAYS")} className="h-4 w-4 text-brand focus:ring-brand" />
                    Broj dana
                  </label>
                </div>
                {shelfLifeMode === "DATE" && (
                  <div className="mt-2">
                    <input
                      type="date"
                      value={shelfLifeDate}
                      onChange={(e) => setShelfLifeDate(e.target.value)}
                      className="h-10 w-48 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-slate-500">Prikaz zavisi od podešavanja pregledača (obično dd.mm.gggg); interno se čuva kao GGGG-MM-DD.</p>
                  </div>
                )}
                {shelfLifeMode === "DAYS" && (
                  <div className="mt-2">
                    <input
                      type="number"
                      min={1}
                      value={shelfLifeDays}
                      onChange={(e) => setShelfLifeDays(e.target.value)}
                      placeholder="npr. 30"
                      className="h-10 w-32 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Broj dana od prijema robe - koristi se kao predlog datuma isteka pri unosu prijema (ne upisuje se automatski).
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {save.isError && (
            <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              Došlo je do greške prilikom čuvanja artikla. Proverite unete podatke.
            </p>
          )}

          <div className="mt-6 flex justify-end gap-3 border-t border-border pt-4">
            <Button variant="secondary" onClick={() => navigate("/artikli")}>
              Otkaži
            </Button>
            <Button disabled={!canSubmit || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Čuvam..." : "Sačuvaj"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

type SubTab = "varijacije" | "dodaci" | "sastojci";

function VarijacijeIDodaciTab({ productId }: { productId?: string }) {
  const [subTab, setSubTab] = useState<SubTab>("varijacije");

  if (!productId) {
    return (
      <EmptyState
        icon={Layers}
        title="Sačuvajte artikal prvo"
        description="Varijacije, dodaci i sastojci za uklanjanje se čuvaju odvojeno - prvo sačuvajte osnovne podatke artikla."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-border">
        {(
          [
            { key: "varijacije", label: "Varijacije" },
            { key: "dodaci", label: "Dodaci" },
            { key: "sastojci", label: "Sastojci za uklanjanje" },
          ] as { key: SubTab; label: string }[]
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              subTab === t.key ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "varijacije" && <VariationsTable productId={productId} />}
      {subTab === "dodaci" && <ProductAttributesSection productId={productId} />}
      {subTab === "sastojci" && <RemovableIngredientsList productId={productId} />}
    </div>
  );
}

function VariationsTable({ productId }: { productId: string }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [priceModifier, setPriceModifier] = useState("");
  const [barcode, setBarcode] = useState("");
  const [sku, setSku] = useState("");

  const { data: variations, isLoading } = useQuery({
    queryKey: ["product-variations", productId],
    queryFn: () => api.listProductVariations(productId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["product-variations", productId] });

  const create = useMutation({
    mutationFn: () =>
      api.createProductVariation(productId, {
        name,
        priceModifier: priceModifier || "0",
        barcode: barcode || null,
        sku: sku || null,
      }),
    onSuccess: () => {
      invalidate();
      setName("");
      setPriceModifier("");
      setBarcode("");
      setSku("");
    },
  });

  const archive = useMutation({
    mutationFn: (variationId: string) => api.updateProductVariation(productId, variationId, { status: "ARCHIVED" }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (variationId: string) => api.deleteProductVariation(productId, variationId),
    onSuccess: invalidate,
  });

  if (isLoading) return <p className="text-xs text-slate-400">Učitavanje varijacija...</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Varijacije su međusobno isključive (npr. Malo/Srednje/Veliko), svaka sa sopstvenom doplatom na
        osnovnu cenu, bar kodom i šifrom. Ovo je odvojeno od "Dodaci" ispod (koji su multi-select doplate).
      </p>
      {variations && variations.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Naziv</th>
              <th>Doplata</th>
              <th>Bar kod</th>
              <th>Šifra</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {variations.map((v: ProductVariationDto) => (
              <tr key={v.id}>
                <td>{v.name}</td>
                <td>{v.priceModifier}</td>
                <td className="text-slate-500">{v.barcode ?? "-"}</td>
                <td className="text-slate-500">{v.sku ?? "-"}</td>
                <td className="text-slate-500">{v.status === "ACTIVE" ? "Aktivna" : "Arhivirana"}</td>
                <td>
                  <div className="flex justify-end gap-2">
                    {v.status === "ACTIVE" && (
                      <button onClick={() => archive.mutate(v.id)} className="text-xs text-slate-400 hover:text-amber-600">
                        Arhiviraj
                      </button>
                    )}
                    <button onClick={() => remove.mutate(v.id)} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-border p-3">
        <div>
          <label className="mb-1 block text-xs text-slate-500">Naziv</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="npr. Veliko" className="h-9 w-36 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Doplata</label>
          <input value={priceModifier} onChange={(e) => setPriceModifier(e.target.value)} placeholder="0.00" className="h-9 w-24 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Bar kod</label>
          <input value={barcode} onChange={(e) => setBarcode(e.target.value)} className="h-9 w-32 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Šifra</label>
          <input value={sku} onChange={(e) => setSku(e.target.value)} className="h-9 w-28 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none" />
        </div>
        <button
          onClick={() => name.trim() && create.mutate()}
          disabled={!name.trim() || create.isPending}
          className="flex h-9 items-center gap-1 rounded-lg border border-border px-3 text-sm hover:bg-app-bg disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden /> Dodaj
        </button>
      </div>
    </div>
  );
}

function RemovableIngredientsList({ productId }: { productId: string }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const { data: ingredients, isLoading } = useQuery({
    queryKey: ["product-removable-ingredients", productId],
    queryFn: () => api.listProductRemovableIngredients(productId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["product-removable-ingredients", productId] });

  const create = useMutation({
    mutationFn: () => api.createProductRemovableIngredient(productId, { name }),
    onSuccess: () => {
      invalidate();
      setName("");
    },
  });

  const remove = useMutation({
    mutationFn: (ingredientId: string) => api.deleteProductRemovableIngredient(productId, ingredientId),
    onSuccess: invalidate,
  });

  if (isLoading) return <p className="text-xs text-slate-400">Učitavanje sastojaka...</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Lista sastojaka koje kupac može tražiti da se uklone (npr. "Bez luka"). Ne utiče na cenu - koristi
        ga poseban POS ekran koji nije deo ovog zadatka.
      </p>
      <ul className="space-y-1.5">
        {ingredients?.map((ing: ProductRemovableIngredientDto) => (
          <li key={ing.id} className="flex items-center gap-2 rounded-md bg-app-bg px-2 py-1.5 text-sm">
            <span className="flex-1">{ing.name}</span>
            <button onClick={() => remove.mutate(ing.id)} className="text-slate-400 hover:text-red-600">
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="npr. Luk"
          className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
        />
        <button
          onClick={() => name.trim() && create.mutate()}
          disabled={!name.trim() || create.isPending}
          className="flex items-center gap-1 rounded-lg border border-border px-3 text-sm hover:bg-app-bg disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden /> Dodaj
        </button>
      </div>
    </div>
  );
}
